import i18n from '@/lib/i18n';

/**
 * Maps backend error messages to translation keys
 * This allows us to translate backend validation errors based on the user's UI language
 */
export function translateBackendError(errorMessage: string): string {
  if (!errorMessage) return errorMessage;

  const message = errorMessage.toLowerCase().trim();

  // Topic validation errors
  if (message.includes('topic must be') && message.includes('characters or less')) {
    const match = errorMessage.match(/(\d+)/);
    const max = match ? match[1] : '100';
    return i18n.t('content:generation.errors.validation.topicTooLong', { max });
  }
  if (message.includes('invalid content detected in topic field')) {
    return i18n.t('content:generation.errors.validation.topicInvalidContent');
  }
  if (message.includes('topic contains inappropriate content')) {
    return i18n.t('content:generation.errors.validation.topicInappropriate');
  }
  if (message.includes('topic contains invalid characters')) {
    return i18n.t('content:generation.errors.validation.topicInvalidChars');
  }

  // Keyword validation errors
  if (message.includes('each keyword must be') && message.includes('characters or less')) {
    const match = errorMessage.match(/(\d+)/);
    const max = match ? match[1] : '20';
    return i18n.t('content:generation.errors.validation.keywordTooLong', { max });
  }
  if (message.includes('invalid content detected in keyword')) {
    return i18n.t('content:generation.errors.validation.keywordInvalidContent');
  }
  if (message.includes('keyword contains inappropriate content')) {
    return i18n.t('content:generation.errors.validation.keywordInappropriate');
  }
  if (message.includes('keyword contains invalid characters')) {
    return i18n.t('content:generation.errors.validation.keywordInvalidChars');
  }
  if (message.includes('maximum') && message.includes('keywords allowed')) {
    const match = errorMessage.match(/(\d+)/);
    const max = match ? match[1] : '5';
    return i18n.t('content:generation.errors.validation.keywordsTooMany', { max });
  }

  // Additional context validation errors
  if (message.includes('additional context must be') && message.includes('characters or less')) {
    const match = errorMessage.match(/(\d+)/);
    const max = match ? match[1] : '1000';
    return i18n.t('content:generation.errors.validation.contextTooLong', { max });
  }
  if (message.includes('invalid content detected in additional context field')) {
    return i18n.t('content:generation.errors.validation.contextInvalidContent');
  }
  if (message.includes('additional context contains inappropriate content')) {
    return i18n.t('content:generation.errors.validation.contextInappropriate');
  }
  if (message.includes('invalid characters detected') && (message.includes('invisible') || message.includes('hidden'))) {
    return i18n.t('content:generation.errors.validation.invisibleChars');
  }

  // Generic invisible character errors
  if (message.includes('invalid characters detected') && (message.includes('invisible') || message.includes('hidden'))) {
    return i18n.t('content:generation.errors.validation.invisibleChars');
  }

  // Country blocked error
  if (message.includes('registration is not available in your country')) {
    return i18n.t('auth:register.errors.countryBlocked');
  }

  // Email verification error
  if (message.includes('verify your email') || message.includes('email verification')) {
    if (message.includes('before logging in') || message.includes('before signing in')) {
      return i18n.t('auth:login.errors.emailNotVerified');
    }
    if (message.includes('to generate content')) {
      return i18n.t('content:generation.emailVerificationRequired');
    }
  }

  // Account locked error
  if (message.includes('account is temporarily locked') || message.includes('account has been locked')) {
    return i18n.t('auth:login.errors.accountLocked');
  }

  // Account deactivated error
  if (message.includes('account is deactivated') || message.includes('account has been deactivated')) {
    return i18n.t('auth:login.errors.accountDeactivated');
  }

  // If no match found, return the original error message
  // This allows for new error types to still be displayed
  return errorMessage;
}

